import type { ApexOptions } from 'apexcharts';

import { useResponsive } from '@aibees/shared/hooks';
import { alpha, useTheme } from '@mui/material/styles';
import { merge } from 'lodash-es';

// ----------------------------------------------------------------------

export default function useChart(
  series?: ApexAxisChartSeries,
  options?: ApexOptions
) {
  const theme = useTheme();

  const smUp = useResponsive('up', 'sm');

  /*  const lastDataIdx = getLastCommonNullIndex(series);

  const firstXaxisWithData = options?.xaxis?.categories?.[(lastDataIdx ?? 0) + 1];

  const LABEL_TOTAL = {
    show: true,
    label: 'Total',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body3.fontSize,
    fontWeight: theme.typography.body3.fontWeight,
    lineHeight: theme.typography.body3.lineHeight,
  };

  const LABEL_VALUE = {
    offsetY: 8,
    color: theme.palette.text.primary,
    fontSize: theme.typography.h3.fontSize,
    fontWeight: theme.typography.h3.fontWeight,
    lineHeight: theme.typography.h3.lineHeight,
  }; */

  const baseOptions: ApexOptions = {
    // Colors
    colors: [
      theme.palette.primary[200],
      theme.palette.warning[200],
      theme.palette.info[200],
      theme.palette.error[300],
      theme.palette.success[300],
      theme.palette.warning[300],
      theme.palette.success[300],
      theme.palette.info[300],
      theme.palette.info[300],
    ],

    // Chart
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      // animations: { enabled: false },
      foreColor: theme.palette.text.disabled,
      fontFamily: theme.typography.fontFamily,
      offsetY: 0,
    },

    // States
    states: {
      hover: {
        filter: {
          type: 'lighten',
          //@ts-ignore
          value: 0.04,
        },
      },
      active: {
        filter: {
          type: 'darken',
          //@ts-ignore
          value: 0.88,
        },
      },
    },

    // Fill
    fill: {
      opacity: 1,
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [0, 100],
      },
    },

    // Datalabels
    dataLabels: {
      enabled: false,
    },

    // Stroke
    stroke: {
      width: 3,
      curve: 'smooth',
      lineCap: 'round',
    },

    // Grid
    grid: {
      strokeDashArray: 3,
      borderColor: theme.palette.divider,
      xaxis: {
        lines: {
          show: false,
        },
      },
    },

    // Xaxis
    xaxis: {
      axisBorder: { show: true },
      axisTicks: { show: false },
    },

    // Markers
    markers: {
      size: 0,
      strokeColors: theme.palette.background.paper,
    },

    // Tooltip
    tooltip: {
      custom(data: any) {
        const currentValues = data.series.map(
          (serie: { [x: string]: any }) => serie[data.dataPointIndex]
        );
        const noData = currentValues.every((i: null | string) => i === null);
        if (noData) return null;
        const seriesColors = data.w.config.colors;

        // Create your custom tooltip content
        let tooltipContent = `
          <div class="custom-tooltip">
            <ul>
        `;

        // Iterate over all series
        data.series.forEach((_serie: any, index: number) => {
          // Get the color for the current series
          const seriesColor = seriesColors[index % seriesColors.length];

          // Add the series name and color to the tooltip content
          if (currentValues[index])
            tooltipContent += `
          <li class="item"><span class="dot" style="background-color: ${seriesColor}"></span>${data.w.config.series[index].name}: ${currentValues[index]}</li>
        `;
        });

        // Close the tooltip content
        tooltipContent += `
            </ul>
            <p class="current-xaxis" >${
              data.w.globals.categoryLabels[data.dataPointIndex]
            }</p>
          </div>
        `;

        // Return the custom tooltip content
        return tooltipContent;
      },
    },

    // Legend
    legend: {
      show: true,
      fontSize: '13',
      position: 'bottom',
      horizontalAlign: 'center',
      offsetY: 20,
      height: 50,
      markers: {
        //radius: 12,
      },
      fontWeight: 500,
      itemMargin: {
        horizontal: 8,
      },
      labels: {
        colors: theme.palette.text.primary,
      },
    },

    // plotOptions
    plotOptions: {
      // Bar
      bar: {
        borderRadius: smUp ? 3 : 1,
        columnWidth: '28%',
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },

      // Pie + Donut
      pie: {
        donut: {
          labels: {
            show: false,
            // value: LABEL_VALUE ,

            // total: LABEL_TOTAL,
          },
        },
      },

      // Radialbar
      radialBar: {
        track: {
          strokeWidth: '100%',
          background: alpha(theme.palette.grey[500], 0.16),
        },
        dataLabels: {
          // value: LABEL_VALUE,
          // total: LABEL_TOTAL,
        },
      },

      // Radar
      radar: {
        polygons: {
          fill: { colors: ['transparent'] },
          strokeColors: theme.palette.divider,
          connectorColors: theme.palette.divider,
        },
      },

      // polarArea
      polarArea: {
        rings: {
          strokeColor: theme.palette.divider,
        },
        spokes: {
          connectorColors: theme.palette.divider,
        },
      },
    },

    // Responsive
    responsive: [
      {
        // sm
        breakpoint: theme.breakpoints.values.sm,
        options: {
          plotOptions: { bar: { columnWidth: '40%' } },
        },
      },
      {
        // mdz
        breakpoint: theme.breakpoints.values.md,
        options: {
          plotOptions: { bar: { columnWidth: '32%' } },
        },
      },
    ],
    // Anotations
    /* annotations: {
      xaxis: [
        {
          x: firstXaxisWithData,
          fillColor: 'white',
          label: {
            text: 'No Data Available for that Time Period',
            textAnchor: 'end',
            position: 'top',
            orientation: 'horizontal',
            offsetY: 43,
            offsetX: -30,
            borderColor: 'transparent',
            style: {
              cssClass: 'apexcharts-xaxis-annotation-label',
              color: theme.palette.grey[400],
              background: 'transparent',
            },
          },
        },
      ],
    }, */
  };

  return merge(baseOptions, options);
}
